import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { logCrossOriginFramedEvent } from "./lib/amplitude-utils";
import { logger } from "./lib/logger";

const getCandidateHostnames = () => {
  const hosts = [];

  // ancestorOrigins (Chrome/Safari)
  if (window.location?.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
    try {
      hosts.push(new URL(window.location.ancestorOrigins[0]).hostname);
    } catch (e) {
      // ignore
    }
  }

  // referrer
  if (document.referrer) {
    try {
      hosts.push(new URL(document.referrer).hostname);
    } catch (e) {
      // ignore
    }
  }

  // window.top.location.hostname (as backward compatibility)
  try {
    const topHost = window.top?.location?.hostname;
    if (topHost) {
      hosts.push(topHost);
    }
  } catch (e) {
    // ignore cross-origin access errors (when document.domain is not set)
  }

  return hosts.filter(Boolean);
};

const isSameTLDPlusOne = (a, b) => {
  const tldPlusOne = (host) => {
    const parts = host.split('.');
    return parts.length > 1 ? parts.slice(-2).join('.') : host;
  };
  return tldPlusOne(a) === tldPlusOne(b);
};

export const getMatchedHostSameTLDPlusOne = () => {
  try {
    const iframeHostname = window.self.location.hostname;
    if (window.self === window.top) {
      return iframeHostname;
    }

    const candidateHosts = getCandidateHostnames();
    const matchedHost = candidateHosts.find((host) => isSameTLDPlusOne(iframeHostname, host)) || null;

    if (!matchedHost) {
      logCrossOriginFramedEvent({
        parentOrigin: document.referrer,
        reason: 'no_matched_host',
      });
    }
    return matchedHost;
  } catch (error) {
    logger.error(`Failed to determine matched host: ${error.message}`);
    logCrossOriginFramedEvent({
      parentOrigin: document.referrer,
      errorMessage: error.message,
      reason: 'matched_host_error',
    });
    return null;
  }
};

const FramingContext = React.createContext({});

export const useFramingContext = () => React.useContext(FramingContext);

export const FrameRestricter = ({ children }) => {
  const matchedHost = useMemo(() => getMatchedHostSameTLDPlusOne(), []);
  const isCrossDomain = useMemo(
    () => window.self !== window.top && !matchedHost,
    [ matchedHost ]
  );
  const [ shouldRenderContent, setShouldRenderContent ] = React.useState(!isCrossDomain);
  React.useEffect(() => {
    async function handleCrossOriginFraming() {
      if (shouldRenderContent && isCrossDomain) {
        await logCrossOriginFramedEvent({
          parentOrigin: matchedHost || document.referrer,
        });
      }
    }
    handleCrossOriginFraming();
  }, [ shouldRenderContent, isCrossDomain, matchedHost ]);

  const framingContextProviderValue = useMemo(() => ({
    allowFraming: () => {
      setShouldRenderContent(true);
    },
    shouldRenderContent,
  }), [ shouldRenderContent ]);

  return (
    <FramingContext.Provider value={framingContextProviderValue}>
      {children}
    </FramingContext.Provider>
  );
};

FrameRestricter.propTypes = {
  children: PropTypes.node.isRequired,
};
